export const OFFICIAL_MARKET_ORDER = ['had', 'hhad', 'ttg', 'crs', 'hafu'];
export const OFFICIAL_POOL_LIMITS = {
    had: 8,
    hhad: 8,
    ttg: 6,
    crs: 4,
    hafu: 4,
};
export const OFFICIAL_CRS_SELECTIONS = [
    's01s00', 's02s00', 's02s01', 's03s00', 's03s01', 's03s02', 's04s00', 's04s01', 's04s02', 's05s00', 's05s01', 's05s02', 's1sh',
    's00s00', 's01s01', 's02s02', 's03s03', 's1sd',
    's00s01', 's00s02', 's01s02', 's00s03', 's01s03', 's02s03', 's00s04', 's01s04', 's02s04', 's00s05', 's01s05', 's02s05', 's1sa',
];
export const OFFICIAL_OPTIONS = {
    had: ['h', 'd', 'a'],
    hhad: ['h', 'd', 'a'],
    ttg: ['s0', 's1', 's2', 's3', 's4', 's5', 's6', 's7'],
    crs: [...OFFICIAL_CRS_SELECTIONS],
    hafu: ['hh', 'hd', 'ha', 'dh', 'dd', 'da', 'ah', 'ad', 'aa'],
};
export const MARKET_LABELS = {
    had: '胜平负',
    hhad: '让球胜平负',
    ttg: '总进球',
    crs: '比分',
    hafu: '半全场',
};
const HAD_LABELS = { h: '主胜', d: '平局', a: '客胜' };
const HAFU_LABELS = {
    hh: '胜胜', hd: '胜平', ha: '胜负', dh: '平胜', dd: '平平', da: '平负', ah: '负胜', ad: '负平', aa: '负负',
};
function roundMoney(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
function padScore(value) {
    return String(value).padStart(2, '0');
}
export function calculatorSelectionLabel(market, selection) {
    if (market === 'had' || market === 'hhad')
        return HAD_LABELS[selection] ?? selection;
    if (market === 'ttg')
        return selection === 's7' ? '7+' : selection.replace(/^s/, '');
    if (market === 'hafu')
        return HAFU_LABELS[selection] ?? selection;
    if (market === 'crs') {
        if (selection === 's1sh')
            return '胜其他';
        if (selection === 's1sd')
            return '平其他';
        if (selection === 's1sa')
            return '负其他';
        const parsed = /^s(\d{2})s(\d{2})$/.exec(selection);
        return parsed ? `${Number(parsed[1])}:${Number(parsed[2])}` : selection;
    }
    return selection;
}
export function calculatorOptionGroups(market) {
    if (market === 'crs') {
        return [
            { label: '胜', options: OFFICIAL_OPTIONS.crs.slice(0, 13) },
            { label: '平', options: OFFICIAL_OPTIONS.crs.slice(13, 18) },
            { label: '负', options: OFFICIAL_OPTIONS.crs.slice(18) },
        ];
    }
    if (market === 'hafu') {
        return [
            { label: '主队半场', options: OFFICIAL_OPTIONS.hafu.slice(0, 3) },
            { label: '半场平', options: OFFICIAL_OPTIONS.hafu.slice(3, 6) },
            { label: '客队半场', options: OFFICIAL_OPTIONS.hafu.slice(6) },
        ];
    }
    return [{ label: MARKET_LABELS[market], options: OFFICIAL_OPTIONS[market] }];
}
function combos(items, size) {
    if (size <= 0 || size > items.length)
        return [];
    if (size === 1)
        return items.map((item) => [item]);
    const result = [];
    const walk = (start, current) => {
        if (current.length === size) {
            result.push([...current]);
            return;
        }
        for (let index = start; index < items.length; index += 1) {
            current.push(items[index]);
            walk(index + 1, current);
            current.pop();
        }
    };
    walk(0, []);
    return result;
}
function uniqueSelections(values) {
    return [...new Set(values.filter(Boolean))];
}
function normalizedPicks(selection) {
    return selection.picks
        .map((pick) => ({ market: pick.market, selections: uniqueSelections(pick.selections) }))
        .filter((pick) => pick.selections.length > 0);
}
function latestQuoteMap(quotes) {
    const map = new Map();
    for (const quote of quotes) {
        if (quote && quote.odds > 1)
            map.set(`${quote.market}:${quote.selection}`, quote);
    }
    return map;
}
function winningSelection(market, match) {
    if (match.status !== 'finished' || match.homeGoals == null || match.awayGoals == null)
        return null;
    const home = Number(match.homeGoals);
    const away = Number(match.awayGoals);
    const diff = home - away;
    const total = home + away;
    if (market === 'had')
        return diff > 0 ? 'h' : diff < 0 ? 'a' : 'd';
    if (market === 'hhad') {
        const line = Number(match.quotes.find((quote) => quote.market === 'hhad')?.lineValue ?? 0);
        const adjusted = diff + line;
        return adjusted > 0 ? 'h' : adjusted < 0 ? 'a' : 'd';
    }
    if (market === 'ttg')
        return total >= 7 ? 's7' : `s${total}`;
    if (market === 'crs') {
        const exact = `s${padScore(home)}s${padScore(away)}`;
        if (OFFICIAL_CRS_SELECTIONS.includes(exact))
            return exact;
        return diff > 0 ? 's1sh' : diff < 0 ? 's1sa' : 's1sd';
    }
    if (market === 'hafu') {
        if (match.halfHomeGoals == null || match.halfAwayGoals == null)
            return null;
        const halfHome = Number(match.halfHomeGoals);
        const halfAway = Number(match.halfAwayGoals);
        const first = halfHome > halfAway ? 'h' : halfHome < halfAway ? 'a' : 'd';
        const second = diff > 0 ? 'h' : diff < 0 ? 'a' : 'd';
        return first + second;
    }
    return null;
}
function payoutCap(passType) {
    if (passType === 2 || passType === 3)
        return 200000;
    if (passType === 4 || passType === 5)
        return 500000;
    if (passType >= 6)
        return 1000000;
    return Number.POSITIVE_INFINITY;
}
function cappedPayout(base, passType) {
    return roundMoney(Math.min(base, payoutCap(passType)));
}
export function calculatorPassConstraint(matches, selections) {
    const matchMap = new Map(matches.map((match) => [match.scheduleId, match]));
    const active = selections
        .map((selection) => ({ selection, picks: normalizedPicks(selection) }))
        .filter((entry) => entry.picks.length > 0 && matchMap.has(entry.selection.scheduleId));
    const safeMatchCount = active.length;
    if (safeMatchCount === 0)
        return { maxPassType: 1, singleOnly: true, reason: '请选择至少一场比赛的赛果' };
    if (active.some((entry) => entry.picks.length > 1)) {
        return { maxPassType: 1, singleOnly: true, reason: '同场多玩法只能计算单关' };
    }
    const markets = active.map((entry) => entry.picks[0].market);
    const sameMarket = markets.every((market) => market === markets[0]);
    const poolLimit = sameMarket ? OFFICIAL_POOL_LIMITS[markets[0]] : 8;
    return {
        maxPassType: Math.min(8, safeMatchCount, poolLimit),
        singleOnly: false,
        reason: safeMatchCount > poolLimit ? `当前玩法最多 ${poolLimit} 场，超出后只能单关` : null,
    };
}
export function calculateLotteryTicket(matches, selections, options) {
    const passType = Math.trunc(Number(options.passType || 1));
    const multiplier = Math.trunc(Number(options.multiplier || 1));
    const matchMap = new Map(matches.map((match) => [match.scheduleId, match]));
    const active = selections
        .map((selection) => ({ selection, picks: normalizedPicks(selection) }))
        .filter((entry) => entry.picks.length > 0 && matchMap.has(entry.selection.scheduleId));
    const missing = [];
    const warnings = [];
    const baseResult = {
        valid: false,
        executable: false,
        bets: 0,
        stake: 0,
        minPayout: 0,
        maxPayout: 0,
        actualPayout: null,
        passType,
        multiplier,
        settlementStatus: 'none',
        missing,
        warnings,
        invalidReason: null,
    };
    if (active.length === 0)
        return { ...baseResult, invalidReason: '请选择至少一场比赛的赛果' };
    if (!Number.isInteger(passType) || passType < 1 || passType > 8)
        return { ...baseResult, invalidReason: '过关方式无效' };
    if (!Number.isInteger(multiplier) || multiplier < 1 || multiplier > 50)
        return { ...baseResult, invalidReason: '倍数必须为 1–50 的整数' };
    const constraint = calculatorPassConstraint(matches, selections);
    if (passType > constraint.maxPassType) {
        return { ...baseResult, invalidReason: constraint.reason ?? `${passType}串1 当前不可执行` };
    }
    if (passType > 1 && active.some((entry) => entry.picks.length > 1)) {
        return { ...baseResult, invalidReason: '同场多玩法只能计算单关' };
    }
    const prepared = active.map((entry) => {
        const match = matchMap.get(entry.selection.scheduleId);
        const liveMap = latestQuoteMap(match.quotes);
        const settledMap = latestQuoteMap(match.settledQuotes ?? []);
        const picks = entry.picks.map((pick) => {
            const odds = pick.selections.map((selection) => {
                const quote = settledMap.get(`${pick.market}:${selection}`) ?? liveMap.get(`${pick.market}:${selection}`);
                if (!quote || !(quote.odds > 1))
                    missing.push(`${entry.selection.scheduleId} ${MARKET_LABELS[pick.market]} ${calculatorSelectionLabel(pick.market, selection)} 未开售`);
                return { selection, odds: quote?.odds ?? null };
            });
            return { ...pick, odds };
        });
        return { match, picks };
    });
    if (missing.length > 0)
        return { ...baseResult, missing, warnings, invalidReason: '存在未开售或缺失 SP 的选项' };
    const allSettled = prepared.every((entry) => entry.match.status === 'finished' && entry.match.homeGoals != null && entry.match.awayGoals != null);
    const settlementStatus = allSettled ? 'settled' : 'pending';
    let bets = 0;
    let minPayout = 0;
    let maxPayout = 0;
    let actualPayout = 0;
    const singlePay = (odds) => roundMoney(2 * odds * multiplier);
    const parlayPay = (oddsProduct) => cappedPayout(2 * oddsProduct * multiplier, passType);
    if (passType === 1) {
        for (const entry of prepared) {
            for (const pick of entry.picks) {
                const values = pick.odds.map((row) => row.odds);
                bets += values.length;
                minPayout += values.reduce((sum, odds) => sum + singlePay(odds), 0);
                maxPayout += values.reduce((sum, odds) => sum + singlePay(odds), 0);
                if (allSettled) {
                    const winner = winningSelection(pick.market, entry.match);
                    if (winner && pick.selections.includes(winner)) {
                        const winningOdds = pick.odds.find((row) => row.selection === winner)?.odds;
                        if (winningOdds)
                            actualPayout += singlePay(winningOdds);
                    }
                }
            }
        }
    }
    else {
        const onePickPerMatch = prepared.map((entry) => ({ ...entry, pick: entry.picks[0] }));
        for (const combo of combos(onePickPerMatch, passType)) {
            const combinationCount = combo.reduce((count, entry) => count * entry.pick.selections.length, 1);
            bets += combinationCount;
            const minOdds = combo.reduce((product, entry) => product * Math.min(...entry.pick.odds.map((row) => row.odds)), 1);
            const maxOdds = combo.reduce((product, entry) => product * Math.max(...entry.pick.odds.map((row) => row.odds)), 1);
            minPayout += parlayPay(minOdds);
            maxPayout += parlayPay(maxOdds);
            if (allSettled) {
                const winners = combo.map((entry) => ({ entry, winner: winningSelection(entry.pick.market, entry.match) }));
                if (winners.every((row) => row.winner && row.entry.pick.selections.includes(row.winner))) {
                    const product = winners.reduce((value, row) => {
                        const odds = row.entry.pick.odds.find((item) => item.selection === row.winner)?.odds ?? 0;
                        return value * odds;
                    }, 1);
                    if (product > 1)
                        actualPayout += parlayPay(product);
                }
            }
        }
    }
    const stake = roundMoney(bets * 2 * multiplier);
    if (stake >= 6000)
        warnings.push('当前投注金额已达到 6000 元，请理性购彩');
    if (passType > 1 && passType > active.length)
        warnings.push(`当前仅选择 ${active.length} 场，无法组成 ${passType}串1`);
    return {
        valid: bets > 0,
        executable: bets > 0,
        bets,
        stake,
        minPayout: roundMoney(minPayout),
        maxPayout: roundMoney(maxPayout),
        actualPayout: allSettled ? roundMoney(actualPayout) : null,
        passType,
        multiplier,
        settlementStatus,
        missing,
        warnings,
        invalidReason: bets > 0 ? null : '当前选择无法形成有效注项',
    };
}
//# sourceMappingURL=lottery-calculator.js.map